/*
 * WHERE THE READER HAS WALKED — the memory the map keeps of one person's reading.
 *
 * The map's promise is that you uncover it by WALKING rather than by filtering: a name is dim until
 * you have been near it, and what you have opened is what the map knows about you. That needs a
 * record, and this is it. Everything downstream reads from here — how brightly a figure draws, what
 * the travel overlay says you have seen, what the trail record shows as your nearest and furthest,
 * and which reading posture the map infers without ever asking.
 *
 * IT STORES NAMES AND COUNTS, NEVER A RUNG. The design is explicit and the reason is worth keeping:
 * a rung index recorded today is a claim about a build, and the rungs are editorial — `tiers.ts` can
 * re-tier a figure in any rebuild. Stored indices would then quietly contradict the map a reader is
 * looking at, saying they had been closest to the divine at a name that is no longer near it. So the
 * record keeps ids, and every rung is looked up from the map in front of the reader, now.
 *
 * NOTHING HERE IS SHARED. It is one browser's memory of one person's reading, kept in localStorage
 * and never sent anywhere. The social plane is a separate thing that meets content at the node id.
 */

/** The storage key. Namespaced, and this module writes no other. */
export const TRAIL_KEY = 'vv.trail';

/**
 * What is remembered, exactly as it is persisted.
 *
 * Deliberately flat and dull — an array and three numbers. It is written to a reader's own browser
 * and read back by a build that may be months newer, so every field has to survive not being
 * understood, and none of them may be a pointer into a graph that has since changed shape.
 */
export interface Trail {
  /** Figure ids the reader has opened, oldest first, without repeats. */
  visited: string[];
  /** Which texts they have read in — the evidence behind "you crossed N corpora". */
  corpora: string[];
  /** How many disputed figures they stopped on. */
  disputed: number;
  /** How many times they opened the text under the translation. */
  originals: number;
}

export function emptyTrail(): Trail {
  return { visited: [], corpora: [], disputed: 0, originals: 0 };
}

/** Add without repeating, keeping first-seen order — the trail is a sequence, not a set. */
function withOut<T>(list: readonly T[], value: T): T[] {
  return list.includes(value) ? [...list] : [...list, value];
}

/** What the reader just did, as the trail needs to hear it. */
export interface Step {
  id: string;
  /** The texts that name the figure they opened. */
  corpora: readonly string[];
  /** Whether its cross-scripture identity is disputed. */
  contested?: boolean;
}

/** Record that the reader opened a figure. */
export function walk(trail: Trail, step: Step): Trail {
  return {
    visited: withOut(trail.visited, step.id),
    corpora: step.corpora.reduce<string[]>((all, c) => withOut(all, c), trail.corpora),
    /* Counted once per figure, not once per opening: a reader who reopens Melchizedek four times
       has stopped where the traditions disagree once, and the standpoint should say so. */
    disputed: trail.disputed + (step.contested && !trail.visited.includes(step.id) ? 1 : 0),
    originals: trail.originals,
  };
}

/** Record that the reader opened the original-language line under a translation. */
export function readOriginal(trail: Trail): Trail {
  return { ...trail, originals: trail.originals + 1 };
}

/**
 * WHAT IS WITHIN REACH: everything tied to something already walked.
 *
 * The frontier. A name beyond it can be looked at but not read — which is what makes the map
 * something you uncover rather than something you filter. Derived on demand from the ties rather
 * than stored, because storing it would be a second copy of the graph that goes stale the moment
 * the graph is rebuilt.
 */
export function reachOf(visited: readonly string[], neighbours: (id: string) => readonly string[]): Set<string> {
  const reach = new Set<string>();
  for (const id of visited) {
    for (const next of neighbours(id)) if (!visited.includes(next)) reach.add(next);
  }
  return reach;
}

/** How brightly a figure draws: what you have walked, what you could walk to, and the rest. */
export type Fog = 'walked' | 'within' | 'unwalked';

export function fogOf(id: string, visited: readonly string[], reach: ReadonlySet<string>): Fog {
  if (visited.includes(id)) return 'walked';
  return reach.has(id) ? 'within' : 'unwalked';
}

/**
 * THE JOURNEY RECORD: the nearest the reader has come to the divine, and the furthest out.
 *
 * Both looked up from the map in front of them — `rungOf` is asked NOW, about the build being
 * looked at. The design records this as learnt the hard way, and it is the whole reason the stored
 * record holds no rung: a figure re-tiered between builds would otherwise leave the record
 * insisting a reader had been somewhere the map no longer says exists.
 *
 * A figure the current build does not carry at all is dropped rather than guessed at.
 */
export function record(
  visited: readonly string[],
  rungOf: (id: string) => number | null,
): { closestIn: string | null; furthestOut: string | null } {
  let closestIn: string | null = null;
  let furthestOut: string | null = null;
  let nearest = Infinity;
  let farthest = -Infinity;
  for (const id of visited) {
    const rung = rungOf(id);
    if (rung === null) continue;
    if (rung < nearest) { nearest = rung; closestIn = id; }
    if (rung > farthest) { farthest = rung; furthestOut = id; }
  }
  return { closestIn, furthestOut };
}

/**
 * HOW THE READER HAS BEEN READING — inferred from what they did, never asked.
 *
 * The design is emphatic that this is not a question: the five self-declared postures exist in its
 * copy and are deliberately not offered as a choice, because asking a reader to pick a label
 * before they have read anything is asking them to perform an identity rather than have one. What
 * they actually did is better evidence and costs them nothing.
 *
 * First match wins, and the order is the claim: crossing traditions outranks stopping at a dispute,
 * which outranks reaching under the translation, which outranks having simply arrived.
 */
export type Standpoint = 'comparatist' | 'sceptic' | 'originals' | 'newcomer';

/** How many texts a reader must have read in before the map calls them a comparatist. */
const CROSSED_ENOUGH = 3;
/** And how many originals opened before it calls them a reader of originals. */
const ORIGINALS_ENOUGH = 3;

export function standpointOf(trail: Trail): Standpoint {
  if (trail.corpora.length >= CROSSED_ENOUGH) return 'comparatist';
  if (trail.disputed >= 1) return 'sceptic';
  if (trail.originals >= ORIGINALS_ENOUGH) return 'originals';
  return 'newcomer';
}

/* ---------- persistence ---------- */

/** Somewhere to keep it. Narrow on purpose, so a test hands over a plain object. */
export interface TrailStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** True when a value read back from storage is a trail and not something else's data. */
function isTrail(value: unknown): value is Trail {
  if (typeof value !== 'object' || value === null) return false;
  const j = value as Partial<Trail>;
  return Array.isArray(j.visited) && j.visited.every(v => typeof v === 'string')
    && Array.isArray(j.corpora) && j.corpora.every(v => typeof v === 'string')
    && typeof j.disputed === 'number' && typeof j.originals === 'number';
}

/**
 * Read the trail back, or start a fresh one.
 *
 * Anything unreadable — absent, corrupt, written by something else, written by a future build in a
 * shape this one does not know — is treated as no trail rather than as an error. A reader whose
 * storage got mangled should meet a map that works, not a page that will not load.
 */
export function readTrail(store: TrailStore | undefined): Trail {
  if (!store) return emptyTrail();
  try {
    const raw = store.getItem(TRAIL_KEY);
    if (!raw) return emptyTrail();
    const parsed: unknown = JSON.parse(raw);
    return isTrail(parsed) ? parsed : emptyTrail();
  } catch {
    return emptyTrail();
  }
}

/** Write it back. A refused write (private mode, quota) loses the memory and nothing else. */
export function writeTrail(store: TrailStore | undefined, trail: Trail): void {
  if (!store) return;
  try {
    store.setItem(TRAIL_KEY, JSON.stringify(trail));
  } catch {
    /* Storage refused. The reader keeps walking; the map simply forgets between visits. */
  }
}

/** The browser's storage, or undefined where there is none (a test, private mode, SSR). */
export function browserStore(): TrailStore | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage;
  } catch {
    return undefined;
  }
}
